import { Project, ProjectDocument } from "types";

export interface FolderOption {
  id: string;
  name: string;
}

export function getAllFoldersFromProject(project: Project): FolderOption[] {
  const foldersID: FolderOption[] = [];
  project.folders.forEach((folder) => {
    foldersID.push({ id: folder.id, name: folder.name });
    folder.sub_folders?.forEach((subFolder) => {
      foldersID.push({
        id: subFolder.id,
        name: `${folder.name} > ${subFolder.name}`,
      });
    });
  });
  return foldersID;
}

export function getUpdatedProjectWithNewDocumentInFolderProvided(
  project: Project,
  folderDestination: string,
  newDocument: ProjectDocument,
): Project {
  const updatedProject = { ...project };
  const folderIndex = updatedProject.folders.findIndex(
    (folder) => folder.id === folderDestination,
  );
  if (folderIndex !== -1) {
    // The document is being added to a main folder
    if (!updatedProject.folders[folderIndex].documents) {
      updatedProject.folders[folderIndex].documents = [];
    }
    updatedProject.folders[folderIndex].documents!.push(newDocument);
  } else {
    // The document is being added to a sub-folder
    updatedProject.folders.forEach((folder) => {
      const subFolderIndex = folder.sub_folders?.findIndex(
        (subFolder) => subFolder.id === folderDestination,
      );
      if (subFolderIndex !== undefined && subFolderIndex !== -1) {
        if (!folder.sub_folders![subFolderIndex].documents) {
          folder.sub_folders![subFolderIndex].documents = [];
        }
        folder.sub_folders![subFolderIndex].documents!.push(newDocument);
      }
    });
  }
  return updatedProject;
}

export function removeDocumentFromProject(
  project: Project,
  document_id: string,
): Project {
  const updatedProject = { ...project };
  updatedProject.folders.forEach((folder) => {
    if (folder.documents) {
      folder.documents = folder.documents.filter(
        (doc) => doc.id !== document_id,
      );
    }
    folder.sub_folders?.forEach((subFolder) => {
      if (subFolder.documents) {
        subFolder.documents = subFolder.documents.filter(
          (doc) => doc.id !== document_id,
        );
      }
    });
  });
  return updatedProject;
}

export function getDocumentById(
  project: Project,
  documentId: string,
): ProjectDocument | undefined {
  for (const folder of project.folders) {
    const doc = folder.documents?.find((d) => d.id === documentId);
    if (doc) return doc;
    if (folder.sub_folders) {
      for (const sub of folder.sub_folders) {
        const subDoc = sub.documents?.find((d) => d.id === documentId);
        if (subDoc) return subDoc;
      }
    }
  }
  return undefined;
}

/**
 * Move a document to a target folder path.
 * @param targetFolderPath - "folderId" for a main folder, "folderId/subFolderId" for a sub-folder
 */
export function moveDocumentToFolder(
  project: Project,
  documentId: string,
  targetFolderPath: string,
): Project {
  const doc = getDocumentById(project, documentId);
  if (!doc) return project;

  const cleaned = removeDocumentFromProject(project, documentId);

  const parts = targetFolderPath.split("/");
  const folderId = parts[0];
  const subFolderId = parts[1];

  return {
    ...cleaned,
    folders: cleaned.folders.map((folder) => {
      if (folder.id === folderId) {
        if (subFolderId) {
          return {
            ...folder,
            sub_folders: folder.sub_folders?.map((sub) =>
              sub.id === subFolderId
                ? { ...sub, documents: [...(sub.documents || []), doc] }
                : sub,
            ),
          };
        }
        return { ...folder, documents: [...(folder.documents || []), doc] };
      }
      return folder;
    }),
  };
}

/** Returns the folder path ("folderId" or "folderId/subFolderId") for a document. */
export function getDocumentFolderPath(
  project: Project,
  documentId: string,
): string | null {
  for (const folder of project.folders) {
    if (folder.documents?.some((d) => d.id === documentId)) return folder.id;
    if (folder.sub_folders) {
      for (const sub of folder.sub_folders) {
        if (sub.documents?.some((d) => d.id === documentId))
          return `${folder.id}/${sub.id}`;
      }
    }
  }
  return null;
}
